import React from "react";

import { BoltIcon } from "@/components/icons";
import { cn } from "@/utils/cn";

import type { CampaignStatus, CreativeSource, MetaPlatform } from "../types";

interface SponsoredBadgeProps {
  platform: MetaPlatform;
  status: CampaignStatus;
  creativeSource?: CreativeSource;
  onClick?: () => void;
  className?: string;
}

const STATUS_LABEL: Record<CampaignStatus, string> = {
  CREATING: "Boosting soon",
  ACTIVE: "Sponsored",
  PAUSED: "Paused",
  COMPLETED: "Completed",
  CANCELLED: "Cancelled",
  FAILED: "Failed",
};

const PLATFORM_INITIAL: Record<MetaPlatform, string> = {
  FACEBOOK: "FB",
  INSTAGRAM: "IG",
};

const TONE: Record<CampaignStatus, string> = {
  CREATING: "bg-blue-600/90 text-white hover:bg-blue-700",
  ACTIVE: "bg-purple-600/90 text-white hover:bg-purple-700",
  PAUSED: "bg-yellow-500/90 text-white hover:bg-yellow-600",
  COMPLETED: "bg-gray-500/90 text-white",
  CANCELLED: "bg-gray-400/90 text-white",
  FAILED: "bg-red-600/90 text-white hover:bg-red-700",
};

export const SponsoredBadge: React.FC<SponsoredBadgeProps> = ({
  platform,
  status,
  creativeSource = "existing_post",
  onClick,
  className,
}) => {
  const Tag = onClick ? "button" : "span";
  const isStandalone = creativeSource === "standalone_ad";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={cn(
        "inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium shadow-sm backdrop-blur-sm transition-colors",
        TONE[status],
        onClick && "cursor-pointer",
        className,
      )}
      title={`${platform} · ${status}${isStandalone ? " · Standalone ad" : ""}`}
    >
      <BoltIcon className="w-3 h-3" />
      <span className="opacity-80 font-semibold">{PLATFORM_INITIAL[platform]}</span>
      <span>·</span>
      <span>{STATUS_LABEL[status]}</span>
      {isStandalone && (
        <span className="ml-1 rounded-sm bg-white/25 px-1 text-[10px] font-semibold uppercase tracking-wide">
          Standalone
        </span>
      )}
    </Tag>
  );
};
